import { datadogRum } from '@datadog/browser-rum';

/**
 * Initialize Datadog Real User Monitoring (RUM)
 *
 * This should be called as early as possible in the application lifecycle
 * to capture all user interactions, errors, and performance metrics.
 */
export const initDatadog = () => {
  // Only initialize if we have the required credentials
  const applicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
  const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;
  const env = process.env.REACT_APP_ENV || 'development';
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  if (!applicationId || !clientToken) {
    console.warn('Datadog RUM: Missing applicationId or clientToken - RUM will not be initialized');
    return;
  }

  datadogRum.init({
    // Application configuration
    applicationId: applicationId,
    clientToken: clientToken,
    site: 'datadoghq.eu',
    service: 'studcorp-front',
    env: env,

    // Version tracking (optional - useful for correlating errors with deployments)
    version: process.env.REACT_APP_VERSION || '1.0.0',

    // Session tracking
    sessionSampleRate: 100, // Track 100% of sessions in all environments
    sessionReplaySampleRate: env === 'prod' ? 20 : 100, // Record 20% in production, 100% in dev/staging
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,

    // Default privacy level for session replay
    defaultPrivacyLevel: 'mask-user-input', // Masks user inputs in session replays

    // Trace correlation with backend
    allowedTracingUrls: [
      // Allow tracing for backend API calls
      (url) => url.startsWith(backendUrl)
    ],

    // Enable tracing to correlate frontend errors with backend traces
    traceSampleRate: 100,

    // Proxy configuration (if needed)
    // proxyUrl: 'https://your-proxy.com',

    // Forward console errors to Datadog
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'],

    // Enhanced telemetry
    enableExperimentalFeatures: ['clickmap'],
  });

  // Start session replay recording
  datadogRum.startSessionReplayRecording();

  console.log(`Datadog RUM initialized for environment: ${env}`);
};

/**
 * Set user context for better error tracking and analytics
 * Call this after user authentication
 *
 * @param {Object} user - User object from authentication
 */
export const setDatadogUser = (user) => {
  if (!user) {
    datadogRum.clearUser();
    return;
  }

  datadogRum.setUser({
    id: user.id?.toString(),
    email: user.email,
    name: user.name ? `${user.name} ${user.lastName || ''}`.trim() : undefined,
    // Add custom attributes
    role: user.roles?.[0],
    school: user.student?.school?.name,
  });
};

/**
 * Add custom global context that will be attached to all RUM events
 *
 * @param {string} key - Context key
 * @param {*} value - Context value
 */
export const addDatadogContext = (key, value) => {
  datadogRum.setGlobalContextProperty(key, value);
};

/**
 * Manually track a custom action/event
 *
 * @param {string} name - Action name
 * @param {Object} context - Additional context
 */
export const trackDatadogAction = (name, context = {}) => {
  datadogRum.addAction(name, context);
};

/**
 * Manually track an error
 *
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export const trackDatadogError = (error, context = {}) => {
  datadogRum.addError(error, context);
};
